import React, { useState } from "react";
import { PermissionWrapper } from "../../../../components/PermissionWrapper/PermissionWrapper";
import type { Placement } from "../../../../api/ads";

import "./PlacementCard.css";

export interface PlacementCardProps {
  placement: Placement;
  onSave: (id: string, config: { frequencyCap: number; frequencyCapPerSession: boolean; maxAdvertisers: number }) => void;
  saveLoading?: boolean;
}

export const PlacementCard: React.FC<PlacementCardProps> = ({
  placement,
  onSave,
  saveLoading = false,
}) => {
  const [perSession, setPerSession] = useState(placement.frequencyCapPerSession);
  const [perUserPerDay, setPerUserPerDay] = useState(String(placement.frequencyCap));
  const [maxAdvertisers, setMaxAdvertisers] = useState(String(placement.maxAdvertisers));

  const handleSave = () => {
    onSave(placement.id, {
      frequencyCap: Number(perUserPerDay) || 0,
      frequencyCapPerSession: perSession,
      maxAdvertisers: Number(maxAdvertisers) || 0,
    });
  };

  return (
    <div className="placement-card">
      {/* Card header: title + description */}
      <div className="placement-card__header">
        <div className="placement-card__title-block">
          <span className="placement-card__title">{placement.name}</span>
          <span className="placement-card__description">{placement.description}</span>
        </div>
      </div>

      {/* Frequency cap section */}
      <div className="placement-card__frequency-section">
        <span className="placement-card__section-heading">Frequency cap</span>
        <div className="placement-card__fields">
          <div className="placement-card__field">
            <label className="placement-card__field-label">Per user / day</label>
            <input
              type="number"
              className="placement-card__field-input"
              value={perUserPerDay}
              min={0}
              disabled={perSession}
              onChange={(e) => setPerUserPerDay(e.target.value)}
            />
            <span className="placement-card__field-helper">
              Maximum times a user can see ads from this placement per day
            </span>
          </div>
          <div className="placement-card__field">
            <label className="placement-card__field-label">Per session</label>
            <div className="placement-card__field-toggle-row">
              <button
                type="button"
                className={`placement-card__toggle ${perSession ? "placement-card__toggle--on" : "placement-card__toggle--off"}`}
                onClick={() => setPerSession((v) => !v)}
              >
                <span className="placement-card__toggle-knob" />
              </button>
              <span className="placement-card__field-helper">
                {perSession ? "Enabled" : "Disabled"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Fields */}
      <div className="placement-card__fields">
        <div className="placement-card__field">
          <label className="placement-card__field-label">Maximum Ads</label>
          <input
            type="number"
            className="placement-card__field-input"
            value={maxAdvertisers}
            min={0}
            onChange={(e) => setMaxAdvertisers(e.target.value)}
          />
          <span className="placement-card__field-helper">
            Maximum number of active ads in this placement
          </span>
        </div>
      </div>

      {/* Save button */}
      <PermissionWrapper anyOfPermissions={["ads.admin", "ads.manage"]} hideWhenDisabled>
        <button
          type="button"
          className={`placement-card__save${saveLoading ? " placement-card__save--disabled" : ""}`}
          onClick={handleSave}
          disabled={saveLoading}
        >
          {saveLoading ? "Saving..." : "Save Changes"}
        </button>
      </PermissionWrapper>
    </div>
  );
};
